import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Truck,
  Wrench,
  RefreshCw,
  Users as UsersIcon,
  Plus,
  X,
  RotateCcw,
  Database,
  Check as CheckIcon,
  Minus as MinusIcon,
  ExternalLink,
} from 'lucide-react';
import { SchedulingSettingsPanel } from '../components/admin/scheduling-settings-panel.js';
import { SalesTypesPanel } from '../components/admin/sales-types-panel.js';
import { PhaseMapPanel } from '../components/admin/phase-map-panel.js';
import { TradePill } from '../components/ui/trade-pill.js';
import { NEUTRAL_COLOR, skillTypes, tradeLabel, useSalesTypes } from '../lib/trade.js';
import { DAY_NAMES } from '../lib/availability.js';
import { HolidaysPanel } from '../components/admin/holidays-panel.js';
import { FieldJobsSettingsPanel } from '../components/admin/field-jobs-settings-panel.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Pill, type PillVariant } from '../components/ui/pill.js';
import { Feedback } from '../components/ui/feedback.js';
import { ConfirmDialog } from '../components/ui/confirm-dialog.js';
import {
  api,
  ApiError,
  type AppUser,
  type CreatedUser,
  type CreateUserBody,
  type ResetPasswordBody,
  type SpireHealth,
  type UpdateUserBody,
  type UserRole,
  type Warehouse,
} from '../lib/api.js';
import { cn } from '../lib/cn.js';
import {
  type Tech,
  type TechTimeOff,
  type TechAvatar,
  type TechTrade,
  type CreateTechBody,
  type UpdateTechBody,
} from '../lib/api.js';
import { useDialogA11y } from '../lib/use-dialog-a11y.js';

/**
 * Operator dashboard.
 *
 * Real-data panels: Spire health, warehouses, Users (Postgres-backed CRUD
 * incl. dispatch-tech links), Skills (techs roster CRUD).
 * Planned panels: Auth & SSO (MFA enforcement copy only — wired to the
 * Cognito/Auth0 provider when we flip on real prod auth), Tinsmith DB
 * (future scope — schema management surface).
 */
export function AdminScreen() {
  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Admin</h1>
        <p className="text-sm text-ink-muted">
          Live Spire connection, fleet reference, and platform configuration.
        </p>
      </header>

      <SpireHealthPanel />

      <SchedulingSettingsPanel />

      <FieldJobsSettingsPanel />

      <SalesTypesPanel />

      <PhaseMapPanel />

      <HolidaysPanel />

      <SpireUdfCheckPanel />

      <UsersPanel />

      <div className="grid gap-3 md:grid-cols-2">
        <WarehousesPanel />
        <SkillsPanel />
      </div>

      {/* Planned panels (Auth & SSO, Tinsmith DB) were here. Hidden until
         the underlying surfaces actually ship — see git history if you
         need the explanatory copy. */}
    </div>
  );
}

function SpireHealthPanel() {
  const healthQuery = useQuery({
    queryKey: ['admin', 'spire-health'],
    queryFn: ({ signal }) => api.admin.spireHealth(signal),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const data: SpireHealth | undefined = healthQuery.data;

  return (
    <div
      className="rounded-lg border bg-surface p-4 space-y-3"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
            style={{ backgroundColor: 'var(--pill-service-bg)', color: 'var(--pill-service-fg)' }}
          >
            <Activity className="h-5 w-5" />
          </span>
          <div>
            <div className="font-medium text-ink">Spire connection</div>
            <div className="text-xs text-ink-muted truncate">
              {data ? `${data.baseUrl} · company ${data.company}` : 'Probing…'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HealthPill data={data} />
          <button
            type="button"
            onClick={() => healthQuery.refetch()}
            disabled={healthQuery.isRefetching}
            aria-label="Re-probe Spire"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${healthQuery.isRefetching ? 'animate-spin' : ''}`} aria-hidden />
          </button>
        </div>
      </div>

      {healthQuery.isError && (
        <Feedback variant="error">
          Couldn't reach the Tinsmith API: {healthQuery.error.message}
        </Feedback>
      )}

      {data?.errorMessage && (
        <Feedback variant="error">{data.errorMessage}</Feedback>
      )}

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <KpiCell
          label="Round-trip"
          value={data ? `${data.latencyMs} ms` : '—'}
          accent={data && data.latencyMs > 1000 ? 'warning' : 'normal'}
        />
        <KpiCell label="Breaker" value={data ? data.breakerState : '—'} />
        <KpiCell
          label="Probed"
          value={data ? relative(data.checkedAt) : '—'}
        />
        <KpiCell label="Auto-refresh" value="30 s" />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1.5">
          Last modified per resource (Spire)
        </div>
        <ul className="space-y-1 text-sm">
          <ModifiedRow label="Customers" value={data?.lastModified.customers ?? null} />
          <ModifiedRow label="Sales orders" value={data?.lastModified.salesOrders ?? null} />
          <ModifiedRow label="Inventory" value={data?.lastModified.inventory ?? null} />
        </ul>
      </div>
    </div>
  );
}

function HealthPill({ data }: { data: SpireHealth | undefined }) {
  if (!data) return <Pill variant="neutral">Probing…</Pill>;
  if (!data.reachable) return <Pill variant="danger" dot>Unreachable</Pill>;
  if (data.breakerState === 'open') return <Pill variant="danger" dot>Breaker open</Pill>;
  if (data.breakerState === 'half_open') return <Pill variant="warning" dot>Half-open</Pill>;
  return <Pill variant="success" dot>Healthy</Pill>;
}

function KpiCell({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'normal' | 'warning';
}) {
  return (
    <div
      className="rounded-md border bg-surface-muted px-3 py-2"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className={`text-base font-semibold tabular-nums ${accent === 'warning' ? 'text-amber-700' : 'text-ink'}`}>
        {value}
      </div>
    </div>
  );
}

function ModifiedRow({ label, value }: { label: string; value: string | null }) {
  return (
    <li className="grid grid-cols-[1fr_auto] gap-2 items-center">
      <span className="text-ink-muted">{label}</span>
      <span className="font-mono text-xs text-ink">
        {value ? `${value.slice(0, 19).replace('T', ' ')}Z` : '—'}
      </span>
    </li>
  );
}

/**
 * The six UDF keys Tinsmith writes on the sales-order header. See
 * CLAUDE.md → "Tinsmith-owned UDF keys" for the canonical reference.
 * Order here drives the display order of the per-key rows below.
 */
const TINSMITH_UDF_KEYS: ReadonlyArray<{ key: string; label: string; hint: string }> = [
  { key: 'fop_tech', label: 'fop_tech', hint: 'Lead tech id' },
  { key: 'fop_date', label: 'fop_date', hint: 'Scheduled day (YYYY-MM-DD)' },
  { key: 'fop_start', label: 'fop_start', hint: 'Slot index, hours past 8 AM' },
  { key: 'fop_duration', label: 'fop_duration', hint: 'Scheduled length in hours' },
  { key: 'fop_completed_at', label: 'fop_completed_at', hint: 'ISO timestamp on clock-out' },
  { key: 'fop_phase', label: 'fop_phase', hint: 'Live field-time-tracking phase' },
  { key: 'fop_helpers', label: 'fop_helpers', hint: 'Comma-wrapped helper tech ids' },
];

/**
 * Spire-UDF sanity panel. Auto-finds a recent Tinsmith-touched order and
 * shows its raw `udf` object so the user can verify writes are landing
 * even when their Spire desktop client hasn't been configured to display
 * `FOP_*` keys (Spire stores unknown UDFs silently — they only render in
 * the desktop UI once added in System Settings → User-Defined Fields).
 *
 * Lets the user search by order # to inspect any specific order. The full
 * raw UDF blob is shown alongside the per-FOP key status — so the user can
 * see what customer-side UDFs (e.g. "SALES TYP") are also on the order.
 */
function SpireUdfCheckPanel() {
  const [orderNo, setOrderNo] = useState('');
  const [debouncedOrderNo, setDebouncedOrderNo] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedOrderNo(orderNo.trim()), 300);
    return () => clearTimeout(id);
  }, [orderNo]);

  // List query — pulls a recent batch of orders with UDF enrichment so we
  // can auto-pick a Tinsmith-touched one when the user hasn't typed an
  // explicit order #. When they DO type, we narrow the same query.
  const listQuery = useQuery({
    queryKey: ['admin', 'spire-udf', 'list', debouncedOrderNo],
    queryFn: ({ signal }) =>
      api.salesOrders.search(
        {
          status: 'open',
          orderNo: debouncedOrderNo || undefined,
          includeUdf: true,
          limit: 40,
        },
        signal,
      ),
    staleTime: 30_000,
  });

  // Auto-pick the most recently-modified order that already has any
  // fop_* key set (non-empty — once the UDFs are defined in Spire every
  // order carries the keys blank) — that's the order most likely to demonstrate the
  // writes. If no Tinsmith order exists yet, fall back to the first
  // order in the list so the dispatcher can still inspect a baseline.
  const candidate = (listQuery.data ?? []).find((o) =>
    Object.entries(o.udf ?? {}).some(([k, v]) => k.toLowerCase().startsWith('fop_') && v !== ''),
  ) ?? listQuery.data?.[0] ?? null;

  return (
    <div
      className="rounded-lg border bg-surface p-4 space-y-3"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
            style={{ backgroundColor: 'var(--pill-pm-bg)', color: 'var(--pill-pm-fg)' }}
          >
            <Database className="h-5 w-5" />
          </span>
          <div>
            <div className="font-medium text-ink">Spire UDF check</div>
            <div className="text-xs text-ink-muted max-w-prose">
              Shows the raw <code className="font-mono">udf</code> object on a
              Tinsmith-touched order. If values appear here but not in Spire
              desktop, define the keys in{' '}
              <span className="font-medium">
                System Settings → User-Defined Fields → Sales Orders
              </span>
              .
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Input
            value={orderNo}
            onChange={(e) => setOrderNo(e.target.value.trim())}
            placeholder="Order # contains…"
            className="h-8 w-40 font-mono text-xs"
            aria-label="Filter by order number"
          />
          <button
            type="button"
            onClick={() => void listQuery.refetch()}
            disabled={listQuery.isFetching}
            aria-label="Re-fetch recent orders"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${listQuery.isFetching ? 'animate-spin' : ''}`} aria-hidden />
          </button>
        </div>
      </div>

      {listQuery.isPending && (
        <Feedback variant="info">Probing recent orders…</Feedback>
      )}
      {listQuery.isError && (
        <Feedback variant="error">
          Couldn't search orders: {listQuery.error.message}
        </Feedback>
      )}

      {listQuery.data && candidate === null && (
        <Feedback variant="info">
          No open orders match. Try clearing the order # filter or schedule
          a job from <code className="font-mono">/operations</code> first.
        </Feedback>
      )}

      {candidate && <SpireUdfOrderDetail spireId={candidate.spireId} fallbackSummary={candidate} />}
    </div>
  );
}

/**
 * Per-order UDF detail card — refetches the full SalesOrder so the udf
 * object reflects the most recent state (the list query's UDF can be
 * a server-merged-summary). Renders the six FOP_* keys with a green
 * check / gray dash, then the entire raw object for context.
 */
function SpireUdfOrderDetail({
  spireId,
  fallbackSummary,
}: {
  spireId: number;
  fallbackSummary: { orderNo: string; customer: { name: string }; modifiedAt: string | null; udf: Record<string, string> };
}) {
  const detailQuery = useQuery({
    queryKey: ['admin', 'spire-udf', 'detail', spireId],
    queryFn: ({ signal }) => api.salesOrders.getById(spireId, signal),
    staleTime: 30_000,
  });

  const udf = detailQuery.data?.udf ?? fallbackSummary.udf ?? {};
  const orderNo = detailQuery.data?.orderNo ?? fallbackSummary.orderNo;
  const customerName = detailQuery.data?.customer.name ?? fallbackSummary.customer.name;
  const modifiedAt = detailQuery.data?.modifiedAt ?? fallbackSummary.modifiedAt;

  const otherKeys = Object.keys(udf)
    .filter((k) => !k.toLowerCase().startsWith('fop_'))
    .sort();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
        <div>
          <span className="font-mono font-semibold text-ink">{orderNo}</span>
          <span className="text-ink-muted"> · {customerName}</span>
        </div>
        <div className="text-ink-subtle">
          {modifiedAt ? `last modified ${relative(modifiedAt)}` : '—'}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1.5">
          Tinsmith UDFs
        </div>
        <ul className="space-y-1 text-sm">
          {TINSMITH_UDF_KEYS.map(({ key, label, hint }) => {
            const value = (udf[key] ?? '').trim();
            const set = value.length > 0;
            return (
              <li
                key={key}
                className="grid grid-cols-[20px_auto_1fr] gap-2 items-center"
              >
                {set ? (
                  <CheckIcon className="h-4 w-4 text-emerald-700" aria-label="Set" />
                ) : (
                  <MinusIcon className="h-4 w-4 text-ink-subtle" aria-label="Empty" />
                )}
                <span className="font-mono text-[12px] text-ink">{label}</span>
                <span className="text-[12px] text-ink-muted font-mono truncate" title={set ? value : hint}>
                  {set ? value : <span className="italic">— {hint}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <details className="text-xs">
        <summary className="cursor-pointer text-ink-muted hover:text-ink select-none">
          Full UDF object ({Object.keys(udf).length} key{Object.keys(udf).length === 1 ? '' : 's'}
          {otherKeys.length > 0 ? ` — ${otherKeys.length} customer-defined` : ''})
        </summary>
        <pre
          className="mt-2 rounded-md border bg-surface-muted p-3 text-[11px] font-mono whitespace-pre overflow-x-auto"
          style={{ borderColor: 'var(--color-border-tertiary)' }}
        >
{JSON.stringify(udf, null, 2)}
        </pre>
      </details>

      <div className="flex items-center justify-between gap-2 text-[11px] flex-wrap">
        <a
          href={`/sales-orders/${spireId}`}
          className="inline-flex items-center gap-1 text-ink-accent hover:underline"
        >
          <ExternalLink className="h-3 w-3" aria-hidden />
          Open {orderNo} in Tinsmith
        </a>
        <span className="text-ink-subtle">
          API: <code className="font-mono">GET /api/v1/sales-orders/{spireId}</code>
        </span>
      </div>
    </div>
  );
}

function WarehousesPanel() {
  const query = useQuery({
    queryKey: ['admin', 'warehouses'],
    queryFn: ({ signal }) => api.admin.warehouses(signal),
    staleTime: 5 * 60_000,
  });

  return (
    <div
      className="rounded-lg border bg-surface p-4"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
            style={{ backgroundColor: 'var(--pill-install-bg)', color: 'var(--pill-install-fg)' }}
          >
            <Truck className="h-5 w-5" />
          </span>
          <div>
            <div className="font-medium text-ink">Warehouses</div>
            <div className="text-xs text-ink-muted">
              From Spire <span className="font-mono">/inventory/warehouses</span>
            </div>
          </div>
        </div>
        {query.data && (
          <span className="text-xs text-ink-muted">{query.data.length} total</span>
        )}
      </div>
      {query.isPending && <SkeletonList rows={6} />}
      {query.isError && <Feedback variant="error" size="sm">{query.error.message}</Feedback>}
      {query.data && (
        <ul className="space-y-1 max-h-80 overflow-y-auto pr-1">
          {query.data.map((w) => (
            <WarehouseRow key={w.spireId} warehouse={w} />
          ))}
        </ul>
      )}
      <div className="mt-3 text-[10.5px] text-ink-subtle">
        Production deployments will map each warehouse to a truck (or stockroom) with min-stock rules.
      </div>
    </div>
  );
}

function WarehouseRow({ warehouse }: { warehouse: Warehouse }) {
  return (
    <li className="grid grid-cols-[auto_1fr_auto] items-center gap-2 text-sm">
      <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-surface-muted text-ink">
        {warehouse.code}
      </span>
      <span className="text-ink truncate">{warehouse.description ?? '—'}</span>
      <Pill variant={warehouse.status === 'active' ? 'success' : 'neutral'}>
        {warehouse.status}
      </Pill>
    </li>
  );
}

// Mirror backend TECH_AVATARS order (techs.service.ts) so the dropdown
// matches the seed roster's reading order.
const TECH_AVATARS: ReadonlyArray<TechAvatar> = ['install', 'service', 'pm', 'emergency', 'sales'];

function SkillsPanel() {
  const queryClient = useQueryClient();
  const salesTypes = useSalesTypes();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<Tech | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState<Tech | null>(null);

  // Own query: dispatch board uses useTechs() which is always active-only.
  // This admin view needs the includeInactive toggle so deactivated rows
  // can be reactivated. invalidate() uses the ['admin','techs'] prefix
  // so it covers both this query AND the dispatch board's useTechs().
  const techsQuery = useQuery({
    queryKey: ['admin', 'techs', { includeInactive }] as const,
    queryFn: ({ signal }) => api.admin.techs.list(includeInactive, signal),
    staleTime: 30_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'techs'] });

  const createMutation = useMutation({
    mutationFn: (body: CreateTechBody) => api.admin.techs.create(body),
    onSuccess: () => {
      setCreating(false);
      void invalidate();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTechBody }) =>
      api.admin.techs.update(id, body),
    onSuccess: () => {
      setEditing(null);
      void invalidate();
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.admin.techs.deactivate(id),
    onSuccess: () => {
      setConfirmingDeactivate(null);
      void invalidate();
    },
  });

  const techs = techsQuery.data ?? [];

  return (
    <div
      className="rounded-lg border bg-surface p-4"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
            style={{ backgroundColor: 'var(--pill-pm-bg)', color: 'var(--pill-pm-fg)' }}
          >
            <Wrench className="h-5 w-5" />
          </span>
          <div>
            <div className="font-medium text-ink">Skills matrix</div>
            <div className="text-xs text-ink-muted">
              Active roster + per-tech trade certifications. Drives the dispatch-board skill-match warning.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-ink-muted cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Show deactivated
          </label>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add tech
          </Button>
        </div>
      </div>

      {techsQuery.isError && (
        <div className="mb-3">
          <Feedback variant="error">{techsQuery.error.message}</Feedback>
        </div>
      )}

      {deactivateMutation.isError && (
        <div className="mb-3">
          <Feedback variant="error">
            Deactivate failed: {deactivateMutation.error.message}
          </Feedback>
        </div>
      )}

      <ul className="space-y-2">
        {techsQuery.isPending &&
          Array.from({ length: 4 }).map((_, i) => (
            <li key={i} className="h-12 bg-surface-muted rounded animate-pulse" />
          ))}
        {!techsQuery.isPending && techs.length === 0 && (
          <li className="text-sm text-ink-muted">
            No active techs. Click <strong>Add tech</strong> to start.
          </li>
        )}
        {techs.map((tech) => (
          <li
            key={tech.id}
            className={cn(
              'flex items-center gap-3 rounded-md border bg-surface px-2.5 py-2',
              !tech.active && 'opacity-70',
            )}
            style={{ borderColor: 'var(--color-border-tertiary)' }}
          >
            <span
              aria-hidden
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-semibold shrink-0"
              style={{
                backgroundColor: AVATAR_BG[tech.avatar],
                color: AVATAR_FG[tech.avatar],
              }}
            >
              {tech.initials}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink truncate">{tech.name}</span>
                {!tech.active && <Pill variant="neutral">Deactivated</Pill>}
              </div>
              <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                {tech.trades.length === 0 && (
                  <span className="text-[11px] text-ink-subtle italic">No trades assigned</span>
                )}
                {tech.trades.map((t) => (
                  <TradePill key={t} types={salesTypes} trade={t} />
                ))}
              </div>
            </div>
            <div className="inline-flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setEditing(tech)}
                className="text-xs text-ink-accent hover:underline"
              >
                Edit
              </button>
              {tech.active ? (
                <button
                  type="button"
                  onClick={() => setConfirmingDeactivate(tech)}
                  className="text-xs text-ink-muted hover:text-red-700 hover:underline"
                >
                  Deactivate
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    updateMutation.mutate({ id: tech.id, body: { active: true } })
                  }
                  className="inline-flex items-center gap-1 text-xs text-ink-accent hover:underline"
                >
                  <RotateCcw className="h-3 w-3" aria-hidden />
                  Reactivate
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-3 text-[10.5px] text-ink-subtle">
        Stored in Tinsmith&apos;s Postgres database (Prisma). Edits land instantly on the
        dispatch board.
      </div>

      {creating && (
        <TechDialog
          title="Add tech"
          submitting={createMutation.isPending}
          error={createMutation.error?.message ?? null}
          initial={{ name: '', initials: '', avatar: 'service', trades: [], workDays: DEFAULT_WORK_DAYS, warehouseCode: '' }}
          onClose={() => setCreating(false)}
          onSubmit={(form) =>
            createMutation.mutate({
              name: form.name,
              initials: form.initials || undefined,
              avatar: form.avatar,
              trades: form.trades,
              workDays: form.workDays,
              warehouseCode: form.warehouseCode || null,
            })
          }
        />
      )}

      {editing && (
        <TechDialog
          title={`Edit ${editing.name}`}
          submitting={updateMutation.isPending}
          error={updateMutation.error?.message ?? null}
          initial={{
            name: editing.name,
            initials: editing.initials,
            avatar: editing.avatar,
            trades: editing.trades,
            workDays: editing.workDays ?? DEFAULT_WORK_DAYS,
            warehouseCode: editing.warehouseCode ?? '',
          }}
          onClose={() => setEditing(null)}
          onSubmit={(form) =>
            updateMutation.mutate({
              id: editing.id,
              body: {
                name: form.name,
                initials: form.initials || undefined,
                avatar: form.avatar,
                trades: form.trades,
                workDays: form.workDays,
                warehouseCode: form.warehouseCode || null,
              },
            })
          }
          techId={editing.id}
        />
      )}

      {confirmingDeactivate && (
        <ConfirmDialog
          title={`Deactivate ${confirmingDeactivate.name}?`}
          message="They'll disappear from the dispatch board's drag-and-drop. Existing assignments stay intact. You can reactivate any time by toggling Show deactivated."
          confirmLabel="Deactivate"
          pending={deactivateMutation.isPending}
          onConfirm={() => deactivateMutation.mutate(confirmingDeactivate.id)}
          onCancel={() => setConfirmingDeactivate(null)}
        />
      )}
    </div>
  );
}

interface TechFormShape {
  name: string;
  initials: string;
  avatar: TechAvatar;
  trades: TechTrade[];
  /** 0 = Sunday .. 6 = Saturday. */
  workDays: number[];
  /** Spire warehouse code of the tech's truck; '' = none. */
  warehouseCode: string;
}

const DEFAULT_WORK_DAYS = [1, 2, 3, 4, 5];

function TechDialog({
  title,
  initial,
  submitting,
  error,
  onSubmit,
  onClose,
  techId,
}: {
  title: string;
  /** Set when editing — enables the time-off list (needs a saved tech). */
  techId?: string;
  initial: TechFormShape;
  submitting: boolean;
  error: string | null;
  onSubmit: (form: TechFormShape) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<TechFormShape>(initial);
  const dialogRef = useDialogA11y<HTMLFormElement>(onClose);
  const salesTypes = useSalesTypes();
  // Active skill types, plus any retired key this tech still carries so it
  // can be seen and un-ticked rather than silently vanishing.
  const skillOptions = [
    ...skillTypes(salesTypes),
    ...form.trades
      .filter((t) => !skillTypes(salesTypes).some((s) => s.key === t))
      .map((t) => ({ key: t, label: `${tradeLabel(salesTypes, t)} (retired)`, color: NEUTRAL_COLOR })),
  ];
  const noTrades = form.trades.length === 0;
  const noName = !form.name.trim();

  const toggleTrade = (t: TechTrade) =>
    setForm((prev) => ({
      ...prev,
      trades: prev.trades.includes(t) ? prev.trades.filter((x) => x !== t) : [...prev.trades, t],
    }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (noName || noTrades) return;
    onSubmit(form);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-40 bg-slate-950/40 flex items-center justify-center p-3"
      onClick={onClose}
    >
      <form
        ref={dialogRef}
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl shadow-xl overflow-hidden"
        style={{ backgroundColor: 'var(--color-elevated-surface)' }}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <span className="text-sm font-medium text-ink">{title}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-4 space-y-3">
          <label className="block text-sm space-y-1">
            <span className="text-xs uppercase tracking-wider text-ink-subtle">Name</span>
            <Input
              required
              data-autofocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Doe"
            />
          </label>
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <label className="block text-sm space-y-1">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">Initials</span>
              <Input
                value={form.initials}
                onChange={(e) => setForm({ ...form, initials: e.target.value.toUpperCase().slice(0, 3) })}
                placeholder="JD"
                maxLength={3}
                className="font-mono"
              />
            </label>
            <label className="block text-sm space-y-1">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">Avatar</span>
              <select
                value={form.avatar}
                onChange={(e) => setForm({ ...form, avatar: e.target.value as TechAvatar })}
                className="h-11 w-full rounded-md border bg-surface px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                style={{ borderColor: 'var(--color-border-secondary)' }}
              >
                {TECH_AVATARS.map((a) => (
                  <option key={a} value={a}>{labelTrade(a)}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="space-y-1">
            <span className="text-xs uppercase tracking-wider text-ink-subtle">
              Skills <span className="text-ink-subtle">(at least one)</span>
            </span>
            <div className="grid grid-cols-2 gap-1.5">
              {skillOptions.map(({ key: t, label, color }) => {
                const active = form.trades.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTrade(t)}
                    aria-pressed={active}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm transition-colors',
                      active
                        ? 'border-brand-200 bg-brand-50 text-ink-accent font-medium'
                        : 'border-line-subtle bg-surface text-ink-muted hover:text-ink',
                    )}
                  >
                    <span
                      aria-hidden
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    {label}
                  </button>
                );
              })}
            </div>
            {noTrades && (
              <Feedback variant="warning" size="sm">
                Pick at least one trade — a tech with no skills can never be dragged onto the board.
              </Feedback>
            )}
          </div>

          <div className="space-y-1">
            <span className="text-xs uppercase tracking-wider text-ink-subtle">Working days</span>
            <div className="flex gap-1">
              {DAY_NAMES.map((label, day) => {
                const on = form.workDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        workDays: on ? prev.workDays.filter((d) => d !== day) : [...prev.workDays, day].sort(),
                      }))
                    }
                    className={cn(
                      'h-9 w-11 rounded-md border text-xs font-medium transition-colors',
                      on
                        ? 'border-brand-200 bg-brand-50 text-ink-accent'
                        : 'border-line-subtle bg-surface text-ink-subtle hover:text-ink',
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <span className="block text-[11px] text-ink-subtle">
              The board shades this tech on other days. Drops there still work — they just warn first.
            </span>
          </div>

          <TruckWarehouseField value={form.warehouseCode} onChange={(v) => setForm((prev) => ({ ...prev, warehouseCode: v }))} />

          {techId && <TimeOffEditor techId={techId} />}
          {error && (
            <Feedback variant="error" size="sm">{error}</Feedback>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t px-4 py-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" disabled={submitting || noTrades || noName}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </footer>
      </form>
    </div>
  );
}


const AVATAR_BG: Record<string, string> = {
  install: '#CECBF6',
  service: '#B5D4F4',
  pm: '#9FE1CB',
  emergency: '#F7C1C1',
  sales: '#FAC775',
};
const AVATAR_FG: Record<string, string> = {
  install: '#26215C',
  service: '#042C53',
  pm: '#04342C',
  emergency: '#501313',
  sales: '#412402',
};

function labelTrade(t: string): string {
  if (t === 'pm') return 'PM';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * Booked time off for one tech — list + add + remove. Lives inside the
 * edit dialog; each change saves immediately (separate from the tech form).
 */
function TimeOffEditor({ techId }: { techId: string }) {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const query = useQuery({
    queryKey: ['admin', 'time-off', techId],
    queryFn: ({ signal }) => api.admin.techs.timeOff({ techId, from: shiftIso(today, -30) }, signal),
  });
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'time-off', techId] });
    void queryClient.invalidateQueries({ queryKey: ['availability'] });
  };
  const add = useMutation<TechTimeOff, ApiError, { startDate: string; endDate: string; startTime?: string; endTime?: string; reason?: string }>({
    mutationFn: (body) => api.admin.techs.addTimeOff(techId, body),
    onSuccess: () => {
      setStart('');
      setEnd('');
      setFromTime('');
      setToTime('');
      setReason('');
      invalidate();
    },
  });
  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) => api.admin.techs.removeTimeOff(id),
    onSuccess: invalidate,
  });
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  // Both blank = the whole day; both set = part of the day (a morning off).
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const timesOk = (!fromTime && !toTime) || (!!fromTime && !!toTime && toTime > fromTime);
  const canAdd = !!start && !!end && end >= start && timesOk && !add.isPending;

  return (
    <div className="space-y-1.5">
      <span className="text-xs uppercase tracking-wider text-ink-subtle">Time off</span>
      <ul className="space-y-1 text-sm">
        {(query.data ?? []).map((t) => (
          <li key={t.id} className="flex items-center gap-2">
            <span className="font-mono text-xs text-ink">
              {t.startDate}
              {t.endDate !== t.startDate ? ` → ${t.endDate}` : ''}
              {t.startTime && t.endTime ? ` · ${t.startTime}–${t.endTime}` : ''}
            </span>
            <span className="text-ink-muted truncate">{t.reason ?? 'Off'}</span>
            <button
              type="button"
              onClick={() => remove.mutate(t.id)}
              disabled={remove.isPending}
              className="ml-auto text-xs text-ink-muted hover:text-ink"
              aria-label="Remove this time off"
            >
              Remove
            </button>
          </li>
        ))}
        {query.isSuccess && query.data.length === 0 && (
          <li className="text-xs text-ink-subtle">No time off booked (last 30 days onward).</li>
        )}
      </ul>
      <div className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-1.5 items-center">
        <Input type="date" aria-label="From" value={start} onChange={(e) => { setStart(e.target.value); if (!end || end < e.target.value) setEnd(e.target.value); }} className="h-9 text-xs" />
        <Input type="date" aria-label="To" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} className="h-9 text-xs" />
        <Input aria-label="Reason" placeholder="Holiday, sick, training…" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={80} className="h-9 text-xs" />
        <Button type="button" size="sm" variant="secondary" disabled={!canAdd} onClick={() => add.mutate({ startDate: start, endDate: end, ...(fromTime && toTime ? { startTime: fromTime, endTime: toTime } : {}), ...(reason.trim() ? { reason: reason.trim() } : {}) })}>
          Add
        </Button>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-ink-muted">
        <span>Only part of the day?</span>
        <Input type="time" step={1800} aria-label="Off from" value={fromTime} onChange={(e) => setFromTime(e.target.value)} className="h-8 w-28 text-xs" />
        <span>to</span>
        <Input type="time" step={1800} aria-label="Off until" value={toTime} min={fromTime || undefined} onChange={(e) => setToTime(e.target.value)} className="h-8 w-28 text-xs" />
        <span className="text-ink-subtle">Leave blank for the whole day.</span>
      </div>
      {!timesOk && (
        <div className="text-[11px] text-amber-700">Set both times, with the end after the start.</div>
      )}
      {(add.isError || remove.isError) && (
        <Feedback variant="error" size="sm">{(add.error ?? remove.error)?.message}</Feedback>
      )}
    </div>
  );
}

/** YYYY-MM-DD ± days, local calendar. */
function shiftIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, (d ?? 1) + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/**
 * Mirrors the Cognito pool's password policy (infra/terraform/modules/auth:
 * 8+ chars, upper, lower, number — symbols optional). Checked client-side so
 * the admin sees WHY a password won't be accepted before submitting; the
 * API maps Cognito's own rejection to a 400 with the same wording as a
 * backstop. Returns null when the password passes.
 */
function passwordPolicyProblem(password: string): string | null {
  const missing: string[] = [];
  if (password.length < 8) missing.push('8+ characters');
  if (!/[A-Z]/.test(password)) missing.push('an uppercase letter');
  if (!/[a-z]/.test(password)) missing.push('a lowercase letter');
  if (!/[0-9]/.test(password)) missing.push('a number');
  return missing.length === 0 ? null : `Needs ${missing.join(', ')}.`;
}

function PasswordPolicyHint({ password }: { password: string }) {
  if (!password) return null;
  const problem = passwordPolicyProblem(password);
  return problem ? (
    <div className="text-[11px] text-amber-700">{problem}</div>
  ) : (
    <div className="text-[11px] text-emerald-700">Meets the password policy.</div>
  );
}

/**
 * Which Spire warehouse is this tech's truck. Drives My truck, transfers
 * and counts on the field side. Spire's warehouse list is small, so a plain
 * select; the current value is kept even if the warehouse has gone inactive.
 */
function TruckWarehouseField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const query = useQuery({
    queryKey: ['admin', 'warehouses'],
    queryFn: ({ signal }) => api.admin.warehouses(signal),
    staleTime: 5 * 60_000,
  });
  const options = query.data ?? [];
  const known = options.some((w) => w.code === value);
  return (
    <label className="block space-y-1 text-sm">
      <span className="text-xs uppercase tracking-wider text-ink-subtle">Truck warehouse</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-md border bg-surface px-2 text-sm"
        style={{ borderColor: 'var(--color-border-secondary)' }}
      >
        <option value="">— no truck (works from main) —</option>
        {!known && value && <option value={value}>{value}</option>}
        {options.map((w) => (
          <option key={w.code} value={w.code}>
            {w.code}
            {w.description ? ` — ${w.description}` : ''}
            {w.status === 'inactive' ? ' (inactive)' : ''}
          </option>
        ))}
      </select>
      <span className="block text-[11px] text-ink-subtle">
        The Spire warehouse holding this tech's van stock. Enables My truck, transfers and truck counts on their phone.
      </span>
    </label>
  );
}

const ROLES: ReadonlyArray<UserRole> = ['admin', 'dispatcher', 'office', 'field_tech'];

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  office: 'Office',
  field_tech: 'Field tech',
};

// Semantic Pill variants so the role chips don't collide *in concept*
// with trade chips (a future surface might show both side-by-side). The
// underlying colors still come from the same palette today, but routing
// through semantic names keeps the call sites stable if we later add
// dedicated --role-* tokens.
const ROLE_VARIANT: Record<UserRole, PillVariant> = {
  admin: 'matrix',     // privileged — purple
  dispatcher: 'info',  // primary office role — blue
  office: 'success',   // back-office — green
  field_tech: 'warning', // field — amber
};

function UsersPanel() {
  const queryClient = useQueryClient();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AppUser | null>(null);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState<AppUser | null>(null);
  const [resettingFor, setResettingFor] = useState<AppUser | null>(null);
  // Once a create succeeds with a passwordHandoff, hold it here so the
  // operator sees it ONCE (the password isn't re-fetchable). Dismissable.
  const [postCreateBanner, setPostCreateBanner] = useState<{ user: CreatedUser } | null>(null);

  const usersKey = ['admin', 'users', { includeInactive }] as const;
  const usersQuery = useQuery({
    queryKey: usersKey,
    queryFn: ({ signal }) => api.admin.users.list(includeInactive, signal),
    staleTime: 30_000,
  });

  // Warehouses for the default-warehouse dropdown in UserDialog. Cached
  // 5 min — shared with WarehousesPanel so the second consumer is free.
  const warehousesQuery = useQuery({
    queryKey: ['admin', 'warehouses'],
    queryFn: ({ signal }) => api.admin.warehouses(signal),
    staleTime: 5 * 60_000,
  });

  // Techs roster for the dispatch-link dropdown in UserDialog. Pull active
  // only (deactivated techs vanish from the dispatch board, so they're
  // invalid link targets). Cached 30s — same staleTime as the panel's own.
  const techsQuery = useQuery({
    queryKey: ['admin', 'techs', { includeInactive: false }] as const,
    queryFn: ({ signal }) => api.admin.techs.list(false, signal),
    staleTime: 30_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });

  const createMutation = useMutation({
    mutationFn: (body: CreateUserBody) => api.admin.users.create(body),
    onSuccess: (created) => {
      setCreateOpen(false);
      void invalidate();
      // Banner is shown whether or not a password came back — when
      // passwordHandoff is null it still confirms "Cognito emailed the
      // invitation to <email>" so the admin knows what happened.
      setPostCreateBanner({ user: created });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateUserBody }) =>
      api.admin.users.update(id, body),
    onSuccess: () => {
      setEditing(null);
      void invalidate();
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.admin.users.deactivate(id),
    onSuccess: () => {
      setConfirmingDeactivate(null);
      void invalidate();
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ResetPasswordBody }) =>
      api.admin.users.resetPassword(id, body),
    onSuccess: () => {
      setResettingFor(null);
    },
  });

  const users = usersQuery.data ?? [];
  const warehouses = warehousesQuery.data ?? [];
  const techs = techsQuery.data ?? [];

  return (
    <div
      className="rounded-lg border bg-surface p-4 space-y-3"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
            style={{ backgroundColor: 'var(--pill-install-bg)', color: 'var(--pill-install-fg)' }}
          >
            <UsersIcon className="h-5 w-5" />
          </span>
          <div>
            <div className="font-medium text-ink">Users &amp; roles</div>
            <div className="text-xs text-ink-muted">
              Stored in Tinsmith&apos;s Postgres database · in production, creating a user
              here provisions their Cognito login
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-ink-muted cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Show deactivated
          </label>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New user
          </Button>
        </div>
      </div>

      {usersQuery.isError && <Feedback variant="error">{usersQuery.error.message}</Feedback>}

      {deactivateMutation.isError && (
        <Feedback variant="error">
          Deactivate failed: {deactivateMutation.error.message}
        </Feedback>
      )}

      {/* Desktop table */}
      <div className="hidden md:block rounded-md border overflow-hidden" style={{ borderColor: 'var(--color-border-tertiary)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr
              className="text-left text-[10px] uppercase tracking-wider text-ink-subtle border-b bg-surface-muted"
              style={{ borderColor: 'var(--color-border-tertiary)' }}
            >
              <th className="px-3 py-2 font-normal">Name &amp; email</th>
              <th className="px-2 py-2 font-normal">Role</th>
              <th className="px-2 py-2 font-normal">Default whse</th>
              <th className="px-2 py-2 font-normal">Status</th>
              <th className="px-2 py-2 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {usersQuery.isPending &&
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={5} className="px-3 py-3">
                    <div className="h-5 bg-surface-muted rounded animate-pulse" />
                  </td>
                </tr>
              ))}
            {!usersQuery.isPending && users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-3 text-sm text-ink-muted">
                  No users yet — click <strong>New user</strong> to add one.
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr
                key={u.id}
                className="border-t"
                style={{ borderColor: 'var(--color-border-tertiary)' }}
              >
                <td className="px-3 py-2">
                  <div className="text-ink font-medium">{u.displayName}</div>
                  <div className="text-[11px] font-mono text-ink-muted">{u.email}</div>
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Pill variant={ROLE_VARIANT[u.role]}>{ROLE_LABEL[u.role]}</Pill>
                    {u.role === 'field_tech' && !u.techId && (
                      // A tech with no dispatch-tech link sees an empty My Day
                      // forever (the day filter matches FOP_TECH against the
                      // LINKED tech id). Warn here instead of fielding the
                      // support ticket.
                      <Pill variant="warning" title="No dispatch tech linked — their My Day will always be empty. Edit the user and pick a tech.">
                        No tech link
                      </Pill>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2 font-mono text-xs text-ink-muted">
                  {u.defaultWarehouse ?? '—'}
                </td>
                <td className="px-2 py-2">
                  {u.active ? (
                    <Pill variant="success" dot>Active</Pill>
                  ) : (
                    <Pill variant="neutral">Deactivated</Pill>
                  )}
                </td>
                <td className="px-2 py-2 text-right">
                  <UserRowActions
                    user={u}
                    onEdit={() => setEditing(u)}
                    onResetPassword={() => setResettingFor(u)}
                    onDeactivate={() => setConfirmingDeactivate(u)}
                    onReactivate={() => updateMutation.mutate({ id: u.id, body: { active: true } })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2">
        {usersQuery.isPending &&
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-24 rounded-md border bg-surface animate-pulse"
              style={{ borderColor: 'var(--color-border-tertiary)' }}
            />
          ))}
        {!usersQuery.isPending && users.length === 0 && (
          <div
            className="rounded-md border bg-surface p-3 text-sm text-ink-muted"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
          >
            No users yet — tap <strong>New user</strong> to add one.
          </div>
        )}
        {users.map((u) => (
          <div
            key={u.id}
            className="rounded-md border bg-surface p-3 space-y-2"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-ink font-medium truncate">{u.displayName}</div>
                <div className="text-[11px] font-mono text-ink-muted truncate">{u.email}</div>
              </div>
              {u.active ? (
                <Pill variant="success" dot>Active</Pill>
              ) : (
                <Pill variant="neutral">Deactivated</Pill>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <Pill variant={ROLE_VARIANT[u.role]}>{ROLE_LABEL[u.role]}</Pill>
              {u.defaultWarehouse && (
                <span className="font-mono text-ink-muted">Whse {u.defaultWarehouse}</span>
              )}
            </div>
            <div className="flex justify-end pt-1">
              <UserRowActions
                user={u}
                onEdit={() => setEditing(u)}
                onResetPassword={() => setResettingFor(u)}
                onDeactivate={() => setConfirmingDeactivate(u)}
                onReactivate={() => updateMutation.mutate({ id: u.id, body: { active: true } })}
              />
            </div>
          </div>
        ))}
      </div>

      {postCreateBanner && (
        <PostCreateBanner
          user={postCreateBanner.user}
          onClose={() => setPostCreateBanner(null)}
        />
      )}

      {createOpen && (
        <UserDialog
          title="New user"
          showPasswordFields
          onClose={() => setCreateOpen(false)}
          submitting={createMutation.isPending}
          error={createMutation.error?.message ?? null}
          warehouses={warehouses}
          techs={techs}
          initial={{
            email: '',
            displayName: '',
            role: 'field_tech',
            defaultWarehouse: '',
            techId: '',
            password: '',
            forcePasswordChange: true,
            sendInviteEmail: true,
          }}
          onSubmit={(form) =>
            createMutation.mutate({
              email: form.email,
              displayName: form.displayName,
              role: form.role,
              defaultWarehouse: form.defaultWarehouse || null,
              techId: form.techId || null,
              ...(form.password ? { password: form.password } : {}),
              forcePasswordChange: form.forcePasswordChange,
              sendInviteEmail: form.sendInviteEmail,
            })
          }
        />
      )}

      {editing && (
        <UserDialog
          title={`Edit ${editing.displayName}`}
          onClose={() => setEditing(null)}
          submitting={updateMutation.isPending}
          error={updateMutation.error?.message ?? null}
          warehouses={warehouses}
          techs={techs}
          emailReadOnly
          initial={{
            email: editing.email,
            displayName: editing.displayName,
            role: editing.role,
            defaultWarehouse: editing.defaultWarehouse ?? '',
            techId: editing.techId ?? '',
            password: '',
            forcePasswordChange: true,
            sendInviteEmail: true,
          }}
          onSubmit={(form) =>
            updateMutation.mutate({
              id: editing.id,
              body: {
                displayName: form.displayName,
                role: form.role,
                defaultWarehouse: form.defaultWarehouse || null,
                techId: form.techId || null,
              },
            })
          }
        />
      )}

      {resettingFor && (
        <ResetPasswordDialog
          user={resettingFor}
          onClose={() => setResettingFor(null)}
          submitting={resetPasswordMutation.isPending}
          error={resetPasswordMutation.error?.message ?? null}
          onSubmit={(password, permanent) =>
            resetPasswordMutation.mutate({
              id: resettingFor.id,
              body: { password, permanent },
            })
          }
        />
      )}

      {confirmingDeactivate && (
        <ConfirmDialog
          title={`Deactivate ${confirmingDeactivate.displayName}?`}
          message={`${confirmingDeactivate.email} will lose access. The account stays in the DB for audit and can be reactivated from this panel.`}
          confirmLabel="Deactivate"
          pending={deactivateMutation.isPending}
          onConfirm={() => deactivateMutation.mutate(confirmingDeactivate.id)}
          onCancel={() => setConfirmingDeactivate(null)}
        />
      )}
    </div>
  );
}

function UserRowActions({
  user,
  onEdit,
  onResetPassword,
  onDeactivate,
  onReactivate,
}: {
  user: AppUser;
  onEdit: () => void;
  onResetPassword: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
}) {
  return (
    <div className="inline-flex items-center gap-3">
      <button
        type="button"
        onClick={onEdit}
        className="text-xs text-ink-accent hover:underline"
      >
        Edit
      </button>
      <button
        type="button"
        onClick={onResetPassword}
        className="text-xs text-ink-muted hover:text-ink hover:underline"
        title="Set a new password for this user"
      >
        Reset password
      </button>
      {user.active ? (
        <button
          type="button"
          onClick={onDeactivate}
          className="text-xs text-ink-muted hover:text-red-700 hover:underline"
        >
          Deactivate
        </button>
      ) : (
        <button
          type="button"
          onClick={onReactivate}
          className="inline-flex items-center gap-1 text-xs text-ink-accent hover:underline"
        >
          <RotateCcw className="h-3 w-3" aria-hidden />
          Reactivate
        </button>
      )}
    </div>
  );
}

interface UserFormShape {
  email: string;
  displayName: string;
  role: UserRole;
  defaultWarehouse: string;
  /** Empty string == not linked. We coerce to null at submit. */
  techId: string;
  /** Optional initial password — only used on create. Empty = Cognito auto-generates + emails. */
  password: string;
  /** Whether the user must change it on first login. */
  forcePasswordChange: boolean;
  /** Whether Cognito sends the invitation email. Ignored when password is supplied. */
  sendInviteEmail: boolean;
}

function UserDialog({
  title,
  initial,
  emailReadOnly,
  showPasswordFields,
  submitting,
  error,
  warehouses,
  techs,
  onSubmit,
  onClose,
}: {
  title: string;
  initial: UserFormShape;
  emailReadOnly?: boolean;
  /** Show password + force-change + send-invite fields. Used on Create. On
   * Edit, password is managed via the separate Reset password action so the
   * Edit dialog stays focused on profile fields. */
  showPasswordFields?: boolean;
  submitting: boolean;
  error: string | null;
  warehouses: ReadonlyArray<Warehouse>;
  techs: ReadonlyArray<Tech>;
  onSubmit: (form: UserFormShape) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<UserFormShape>(initial);
  const dialogRef = useDialogA11y<HTMLFormElement>(onClose);

  // Only surface active warehouses for new picks, but if the user's
  // current value happens to point at a deactivated warehouse, keep
  // showing it so their stored selection doesn't silently disappear.
  const activeWarehouses = warehouses.filter((w) => w.status === 'active');
  const currentMissing =
    form.defaultWarehouse &&
    !activeWarehouses.some((w) => w.code === form.defaultWarehouse);
  const currentMatch = currentMissing
    ? warehouses.find((w) => w.code === form.defaultWarehouse)
    : undefined;

  // Same pattern for the tech link: only show active techs as pick
  // options, but if the user is currently linked to a deactivated tech
  // surface it so the operator can see + clear the dangling link.
  const activeTechs = techs.filter((t) => t.active);
  const techMissing =
    form.techId && !activeTechs.some((t) => t.id === form.techId);
  const techStaleMatch = techMissing
    ? techs.find((t) => t.id === form.techId)
    : undefined;

  const handle = (e: FormEvent) => {
    e.preventDefault();
    if (!form.email.trim() || !form.displayName.trim()) return;
    onSubmit(form);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-40 bg-slate-950/40 flex items-center justify-center p-3"
      onClick={onClose}
    >
      <form
        ref={dialogRef}
        onSubmit={handle}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl shadow-xl space-y-3 overflow-hidden"
        style={{ backgroundColor: 'var(--color-elevated-surface)' }}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <span className="text-sm font-medium text-ink">{title}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-4 space-y-3">
          <label className="block text-sm space-y-1">
            <span className="text-xs uppercase tracking-wider text-ink-subtle">Email</span>
            <Input
              type="email"
              required
              readOnly={emailReadOnly}
              data-autofocus={emailReadOnly ? undefined : true}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="name@hvac.example"
              className={emailReadOnly ? 'opacity-60' : ''}
            />
          </label>
          <label className="block text-sm space-y-1">
            <span className="text-xs uppercase tracking-wider text-ink-subtle">Display name</span>
            <Input
              required
              data-autofocus={emailReadOnly ? true : undefined}
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder="Jane Doe"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm space-y-1">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">Role</span>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
                className="h-11 w-full rounded-md border bg-surface px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                style={{ borderColor: 'var(--color-border-secondary)' }}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm space-y-1">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">Default warehouse</span>
              <select
                value={form.defaultWarehouse}
                onChange={(e) => setForm({ ...form, defaultWarehouse: e.target.value })}
                className="h-11 w-full rounded-md border bg-surface px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-300"
                style={{ borderColor: 'var(--color-border-secondary)' }}
              >
                <option value="">— None —</option>
                {activeWarehouses.map((w) => (
                  <option key={w.spireId} value={w.code}>
                    {w.code}
                    {w.description ? ` · ${w.description}` : ''}
                  </option>
                ))}
                {currentMatch && (
                  <option value={currentMatch.code}>
                    {currentMatch.code} · (deactivated)
                  </option>
                )}
              </select>
              {warehouses.length === 0 && (
                <span className="text-[10.5px] text-ink-subtle">
                  Warehouses load from Spire; if this list is empty the connection failed —
                  see the Spire panel above.
                </span>
              )}
            </label>
          </div>
          {showPasswordFields && (
            <div
              className="rounded-md border p-3 space-y-2"
              style={{ borderColor: 'var(--color-border-tertiary)' }}
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-ink-subtle font-medium">
                  Initial password
                </span>
                <span className="text-[10.5px] text-ink-subtle">
                  Leave blank → Cognito auto-generates and emails
                </span>
              </div>
              <Input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="8+ chars with upper, lower and a number"
                className="font-mono"
                autoComplete="new-password"
              />
              <PasswordPolicyHint password={form.password} />
              {form.password && (
                <label className="flex items-center gap-2 text-[12.5px] text-ink">
                  <input
                    type="checkbox"
                    checked={form.forcePasswordChange}
                    onChange={(e) => setForm({ ...form, forcePasswordChange: e.target.checked })}
                    className="h-4 w-4"
                  />
                  Force user to change this password on first login
                </label>
              )}
              {!form.password && (
                <label className="flex items-center gap-2 text-[12.5px] text-ink">
                  <input
                    type="checkbox"
                    checked={form.sendInviteEmail}
                    onChange={(e) => setForm({ ...form, sendInviteEmail: e.target.checked })}
                    className="h-4 w-4"
                  />
                  Send Cognito invitation email with the temporary password
                </label>
              )}
              {form.password && !form.forcePasswordChange && (
                <div className="text-[10.5px] text-ink-subtle">
                  ⚠ The user will log in with the exact password above. Make sure you share it through a secure channel.
                </div>
              )}
            </div>
          )}

          <label className="block text-sm space-y-1">
            <span className="text-xs uppercase tracking-wider text-ink-subtle">
              Dispatch tech link
            </span>
            <select
              value={form.techId}
              onChange={(e) => setForm({ ...form, techId: e.target.value })}
              className="h-11 w-full rounded-md border bg-surface px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              style={{ borderColor: 'var(--color-border-secondary)' }}
            >
              <option value="">— Not linked (no field assignments) —</option>
              {activeTechs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} · {t.initials}
                </option>
              ))}
              {techStaleMatch && (
                <option value={techStaleMatch.id}>
                  {techStaleMatch.name} · (deactivated)
                </option>
              )}
            </select>
            <span className="text-[10.5px] text-ink-subtle">
              Field techs only see "My day" stops where the order's FOP_TECH UDF
              matches their linked tech. Office and dispatcher roles don't need this.
            </span>
          </label>
          {error && (
            <Feedback variant="error" size="sm">{error}</Feedback>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t px-4 py-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={submitting || (showPasswordFields && !!form.password && passwordPolicyProblem(form.password) !== null)}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

/**
 * One-time post-create banner. Surfaces the admin-supplied password (so
 * they can copy it off-screen to email/SMS the user) OR confirms that
 * Cognito sent the invitation. The banner is dismissable; once closed
 * the password is unrecoverable — the admin would have to use Reset
 * password.
 */
function PostCreateBanner({
  user,
  onClose,
}: {
  user: CreatedUser;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    if (!user.passwordHandoff) return;
    try {
      await navigator.clipboard.writeText(user.passwordHandoff);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be blocked — admin can hand-copy */
    }
  };
  return (
    <div
      className="rounded-md border-2 px-3 py-2.5 flex items-start gap-3"
      style={{
        borderColor: 'var(--feedback-success-border)',
        backgroundColor: 'var(--feedback-success-bg)',
      }}
    >
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="text-sm font-medium text-ink">
          {user.passwordHandoff
            ? `User created — share this password with ${user.displayName} (${user.email})`
            : `User created — Cognito emailed an invitation to ${user.email}`}
        </div>
        {user.passwordHandoff && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm bg-surface px-2 py-1 rounded border" style={{ borderColor: 'var(--color-border-tertiary)' }}>
              {user.passwordHandoff}
            </span>
            <button
              type="button"
              onClick={() => void onCopy()}
              className="text-xs text-ink-accent hover:underline"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}
        <div className="text-[11px] text-ink-muted">
          This message disappears on dismiss. You can't recover the password later — use Reset password.
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function ResetPasswordDialog({
  user,
  submitting,
  error,
  onSubmit,
  onClose,
}: {
  user: AppUser;
  submitting: boolean;
  error: string | null;
  onSubmit: (password: string, permanent: boolean) => void;
  onClose: () => void;
}) {
  const dialogRef = useDialogA11y<HTMLFormElement>(onClose);
  const [password, setPassword] = useState('');
  const [permanent, setPermanent] = useState(true);
  const handle = (e: FormEvent) => {
    e.preventDefault();
    if (!password) return;
    onSubmit(password, permanent);
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Reset password for ${user.displayName}`}
      className="fixed inset-0 z-40 bg-slate-950/40 flex items-center justify-center p-3"
      onClick={onClose}
    >
      <form
        ref={dialogRef}
        onSubmit={handle}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl shadow-xl space-y-3 overflow-hidden"
        style={{ backgroundColor: 'var(--color-elevated-surface)' }}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <span className="text-sm font-medium text-ink">Reset password for {user.displayName}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-4 space-y-3">
          <div className="text-xs text-ink-muted">
            {user.email}
          </div>
          <label className="block text-sm space-y-1">
            <span className="text-xs uppercase tracking-wider text-ink-subtle">New password</span>
            <Input
              type="text"
              required
              data-autofocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8+ chars with upper, lower and a number"
              className="font-mono"
              autoComplete="new-password"
            />
            <PasswordPolicyHint password={password} />
          </label>
          <label className="flex items-center gap-2 text-[12.5px] text-ink">
            <input
              type="checkbox"
              checked={permanent}
              onChange={(e) => setPermanent(e.target.checked)}
              className="h-4 w-4"
            />
            User can log in with this password immediately (no forced change)
          </label>
          {!permanent && (
            <div className="text-[10.5px] text-ink-subtle">
              ⚠ The user will be forced to change the password on their next login.
            </div>
          )}
          {error && <Feedback variant="error" size="sm">{error}</Feedback>}
        </div>
        <footer className="flex justify-end gap-2 border-t px-4 py-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={submitting || !password || passwordPolicyProblem(password) !== null}>
            {submitting ? 'Setting…' : 'Set password'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

function SkeletonList({ rows }: { rows: number }) {
  return (
    <ul className="space-y-1.5" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="h-6 bg-surface-muted rounded animate-pulse" />
      ))}
    </ul>
  );
}

function relative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const ms = Date.now() - then;
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}
